import { APP_NAME } from "@xop-any-time/shared";
import { useEffect } from "react";

import type { PageMeta } from "./content.js";

/**
 * The document head for a public page: `<title>` as "<page> · XOP Reseller"
 * and one `<meta name="description">`, upserted rather than appended so a
 * client-side navigation never leaves two.
 *
 * On unmount the title falls back to the bare product name and the
 * description is removed — the signed-in portal sets neither, and a stale
 * "Reseller Program" tab title over the invoices page would be wrong.
 *
 * `index.html` ships the landing page's description so the very first paint
 * has one before React mounts; this hook then keeps it in step per page.
 */
export function usePageMeta({ title, description }: PageMeta): void {
  useEffect(() => {
    document.title = `${title} · ${APP_NAME}`;
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = description;
    return () => {
      document.title = APP_NAME;
      document.querySelector('meta[name="description"]')?.remove();
    };
  }, [title, description]);
}
